import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Users, Eye, UserPlus, Shield, Building2 } from "lucide-react";
import { toast } from "sonner";

const TIER_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: "Tier 1 — First Responders", color: "bg-blue-50 text-blue-700 border-blue-200" },
  2: { label: "Tier 2 — Escalation", color: "bg-amber-50 text-amber-700 border-amber-200" },
  3: { label: "Tier 3 — Final Escalation", color: "bg-red-50 text-red-700 border-red-200" },
  4: { label: "Tier 4", color: "bg-purple-50 text-purple-700 border-purple-200" },
  5: { label: "Tier 5", color: "bg-gray-50 text-gray-700 border-gray-200" },
};

const AGENT_TYPE_ICONS: Record<string, typeof Users> = {
  user: UserPlus,
  role: Shield,
  department: Building2,
};

export default function QueueAgentSettings() {
  const { data: queues = [] } = trpc.tickets.ticketQueues.getAll.useQuery();
  const { data: usersData } = trpc.users.list.useQuery({});
  const allUsers = usersData?.users ?? [];
  const [selectedQueueId, setSelectedQueueId] = useState<string>("");

  const { data: agents = [], isLoading, refetch } = trpc.tickets.queueAgents.getByQueue.useQuery(
    { queueId: Number(selectedQueueId) },
    { enabled: !!selectedQueueId },
  );

  const createMut = trpc.tickets.queueAgents.create.useMutation({
    onSuccess: () => { refetch(); setAddOpen(false); toast.success("Agent added to queue"); },
  });
  const deleteMut = trpc.tickets.queueAgents.delete.useMutation({
    onSuccess: () => { refetch(); toast.success("Agent removed from queue"); },
  });
  const updateMut = trpc.tickets.queueAgents.update.useMutation({
    onSuccess: () => { refetch(); toast.success("Agent updated"); },
  });

  // Preview
  const [previewTier, setPreviewTier] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { data: previewAgents = [], isFetching: previewLoading } = trpc.tickets.queueAgents.previewResolved.useQuery(
    { queueId: Number(selectedQueueId), tier: previewTier },
    { enabled: previewOpen && !!selectedQueueId },
  );

  // Add agent form
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    agentType: "user" as "user" | "role" | "department",
    agentReference: "",
    tier: 1,
    maxLoad: 20,
  });

  const selectedQueue = queues.find((q: any) => String(q.id) === selectedQueueId);

  // Group agents by tier
  const tiers = new Map<number, typeof agents>();
  for (const agent of agents) {
    const tier = agent.tier;
    if (!tiers.has(tier)) tiers.set(tier, []);
    tiers.get(tier)!.push(agent);
  }
  const sortedTiers = Array.from(tiers.entries()).sort((a, b) => a[0] - b[0]);

  const handleAddAgent = () => {
    if (!selectedQueueId || !form.agentReference) return;
    createMut.mutate({
      queueId: Number(selectedQueueId),
      agentType: form.agentType,
      agentReference: form.agentReference,
      tier: form.tier,
      maxLoad: form.maxLoad,
    });
  };

  const openPreview = (tier: number) => {
    setPreviewTier(tier);
    setPreviewOpen(true);
  };

  return (
    <div className="px-0 py-5 space-y-5">

      {/* Queue selector */}
      <div className="flex gap-3 items-end">
        <div className="w-72">
          <label className="text-sm font-medium text-[#2C2C2C] block mb-1">Select Queue</label>
          <Select value={selectedQueueId} onValueChange={setSelectedQueueId}>
            <SelectTrigger><SelectValue placeholder="Choose a queue to configure" /></SelectTrigger>
            <SelectContent>{queues.map((q: any) => <SelectItem key={q.id} value={String(q.id)}>{q.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {selectedQueue && (
          <div className="flex gap-2 pb-0.5">
            <Badge variant="outline" className="text-xs">{(selectedQueue as any).assignmentMode?.replace("_", " ")}</Badge>
          </div>
        )}
      </div>

      {selectedQueueId && (
        <>
          {/* Action bar */}
          <div className="flex gap-2">
            <Button onClick={() => { setForm({ agentType: "user", agentReference: "", tier: 1, maxLoad: 20 }); setAddOpen(true); }} className="bg-[#5B2C93] hover:bg-[#4A2377] text-white">
              <Plus className="h-4 w-4 mr-2" />Add Agent
            </Button>
          </div>

          {/* Agent list grouped by tier */}
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
          ) : sortedTiers.length === 0 ? (
            <div className="bg-white rounded-lg border border-[#E0E0E0] p-12 text-center">
              <Users className="h-12 w-12 mx-auto text-[#D1D5DB] mb-3" />
              <p className="text-[#6B6B6B] font-medium">No agents configured for this queue</p>
              <p className="text-sm text-[#9CA3AF] mt-1">Add agents to enable auto-assignment. Tickets will be unassigned until agents are configured.</p>
            </div>
          ) : (
            sortedTiers.map(([tier, tierAgents]) => {
              const tierInfo = TIER_LABELS[tier] || { label: `Tier ${tier}`, color: "bg-gray-50 text-gray-700 border-gray-200" };
              return (
                <div key={tier} className="bg-white rounded-lg border border-[#E0E0E0] overflow-hidden">
                  {/* Tier header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b bg-[#FAFAFA]">
                    <div className="flex items-center gap-2">
                      <Badge className={`${tierInfo.color} border`}>{tierInfo.label}</Badge>
                      <span className="text-xs text-[#9CA3AF]">{tierAgents.length} agent(s)</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openPreview(tier)} className="text-xs">
                      <Eye className="h-3.5 w-3.5 mr-1" />Preview Resolved
                    </Button>
                  </div>

                  {/* Agent rows */}
                  <table className="w-full text-sm">
                    <thead><tr className="border-b">
                      <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B] uppercase">Type</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B] uppercase">Agent</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B] uppercase">Max Load</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B] uppercase">Status</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-[#6B6B6B] uppercase">Actions</th>
                    </tr></thead>
                    <tbody>
                      {tierAgents.map((agent: any) => {
                        const Icon = AGENT_TYPE_ICONS[agent.agentType] || Users;
                        return (
                          <tr key={agent.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5">
                                <Icon className="h-3.5 w-3.5 text-[#6B6B6B]" />
                                <span className="capitalize text-xs">{agent.agentType}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 font-medium">{agent.displayName}</td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs text-[#6B6B6B]">{agent.maxLoad} tickets</span>
                            </td>
                            <td className="px-4 py-2.5">
                              <Badge variant="outline" className={agent.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}>
                                {agent.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-right flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={() => updateMut.mutate({ id: agent.id, isActive: !agent.isActive })}
                              >
                                {agent.isActive ? "Disable" : "Enable"}
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteMut.mutate({ id: agent.id })}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </>
      )}

      {/* Add Agent Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Agent to Queue</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Agent Type</label>
              <Select value={form.agentType} onValueChange={(v: any) => setForm(f => ({ ...f, agentType: v, agentReference: "" }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Individual User</SelectItem>
                  <SelectItem value="role">System Role (all users with role)</SelectItem>
                  <SelectItem value="department">Department (all users in dept)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#9CA3AF] mt-1">
                {form.agentType === "user" && "Select a specific user to handle tickets"}
                {form.agentType === "role" && "All active users with this role will be included in the agent pool"}
                {form.agentType === "department" && "All active users in this department will be included"}
              </p>
            </div>

            {form.agentType === "user" ? (
              <div>
                <label className="text-sm font-medium">Select User</label>
                <Select value={form.agentReference} onValueChange={v => setForm(f => ({ ...f, agentReference: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choose a user" /></SelectTrigger>
                  <SelectContent>
                    {allUsers
                      .filter((u: any) => u.status === "active")
                      .map((u: any) => (
                        <SelectItem key={u.id} value={String(u.id)}>{u.name || u.email || `User #${u.id}`}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            ) : form.agentType === "role" ? (
              <div>
                <label className="text-sm font-medium">Role</label>
                <Select value={form.agentReference} onValueChange={v => setForm(f => ({ ...f, agentReference: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choose a role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium">Department ID</label>
                <Input
                  type="number"
                  value={form.agentReference}
                  onChange={e => setForm(f => ({ ...f, agentReference: e.target.value }))}
                  placeholder="Enter department ID"
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Tier</label>
              <Select value={String(form.tier)} onValueChange={v => setForm(f => ({ ...f, tier: Number(v) }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(t => (
                    <SelectItem key={t} value={String(t)}>{TIER_LABELS[t]?.label || `Tier ${t}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#9CA3AF] mt-1">
                Tier 1 agents receive tickets first. Higher tiers are used when SLA escalation triggers auto-reassignment.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Max Load (for load balance mode)</label>
              <Input
                type="number"
                value={form.maxLoad}
                onChange={e => setForm(f => ({ ...f, maxLoad: Number(e.target.value) }))}
                className="mt-1"
              />
              <p className="text-xs text-[#9CA3AF] mt-1">Maximum number of open tickets this agent can be assigned before being skipped.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddAgent}
              disabled={!form.agentReference || createMut.isPending}
              className="bg-[#5B2C93] hover:bg-[#4A2377] text-white"
            >
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Resolved Agents Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolved Agents — {TIER_LABELS[previewTier]?.label || `Tier ${previewTier}`}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#6B6B6B]">These are the actual users who would be eligible for ticket assignment at this tier:</p>
          <div className="max-h-64 overflow-y-auto">
            {previewLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#5B2C93]" /></div>
            ) : previewAgents.length === 0 ? (
              <p className="text-sm text-[#9CA3AF] text-center py-8">No active users resolved for this tier</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b">
                  <th className="text-left px-3 py-2 text-xs font-medium text-[#6B6B6B]">Name</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-[#6B6B6B]">Email</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-[#6B6B6B]">Source</th>
                </tr></thead>
                <tbody>
                  {previewAgents.map((a: any) => (
                    <tr key={a.userId} className="border-b border-[#F0F0F0]">
                      <td className="px-3 py-2 font-medium">{a.name}</td>
                      <td className="px-3 py-2 text-[#6B6B6B]">{a.email}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-xs capitalize">{a.source}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
